// ============= Imports =============
import { tokenManager } from '@tw/tw-component-library';

// ============= Constants =============
const BASE_URL = import.meta.env.VITE_BASE_CORE_URL;
const API_ENDPOINT = '/what_to_do';
const LITIUM_SCOPE = import.meta.env.VITE_LITIUM_CORE_SCOPE;

const HEADERS = {};

// ============= Types =============

export enum Action {
  CREATE = 'create',
  OPEN = 'open',
}

export enum Feature {
  ASK = 'ask',
  COLLECTION = 'collection',
  DOCUMENT = 'document',
  PERSONA = 'persona',
  PROMPT = 'prompt',
  WORKFLOWINSTANCE = 'workflowinstance',
  WORKSPACE = 'workspace',
}

export interface ResultOption {
  action: Action;
  description: string;
  feature: Feature;
  id: string;
  workspaceId: string;
  /** Optional helpful prompt text that can be surfaced in the UI */
  prompt?: string;
}

interface PostWhatToDoProps {
  getAccessToken: (scopes?: string[]) => Promise<string | null>;
  userInput: string;
}

interface PostWhatToDoResponse {
  success: boolean;
  data: { resultOptions: ResultOption[] } | ResultOption[];
  error?: string;
}

// ============= Utility Functions =============
/**
 * Constructs filter parameters for API query
 * @param searchValue Search term to filter users
 * @returns Formatted filter string
 */
// const constructFilterParams = (searchValue: string): string => {
//   return [
//     `startswith(givenName,'${searchValue}')`,
//     `startswith(surname,'${searchValue}')`,
//     `startswith(userPrincipalName,'${searchValue}')`,
//   ].join(' or ');
// };

/**
 * Creates headers for API request
 * @param token Authentication token
 * @returns Headers object
 */
const createHeaders = (token: string) => ({
  Authorization: `Bearer ${token}`,
  ...HEADERS,
  'Content-Type': 'application/json',
});

// ============= Main Function =============
/**
 * Posts what to do to the API
 * @param getAccessToken Function to acquire access token
 * @param userInput User input string
 * @returns Promise with API response containing what to do data or error
 */
export const postWhatToDo = async ({
  getAccessToken,
  userInput,
}: PostWhatToDoProps): Promise<PostWhatToDoResponse> => {

  try {
    console.log('🔄 Posting what to do...');

    // Token acquisition with required scopes
    const token = await getAccessToken([LITIUM_SCOPE]);
    if (!token) {
      throw new Error('Failed to acquire token');
    }
    tokenManager.logTokenInfo(token, 'API');
    // URL construction
    const url = `${BASE_URL}${API_ENDPOINT}`;
    const convertURI = encodeURI(url);
    console.log('🌐 Making request to:', convertURI);

    // API request
    const response = await fetch(convertURI, {
      method: 'POST',
      headers: createHeaders(token),
      body: JSON.stringify({ userInput: userInput }),
    });
    // Response handling
    if (!response.ok) {
      console.log(`HTTP error! status: ${response.status}`);
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    console.log(`✅ API data fetched successfully: ${data}`);

    return {
      success: true,
      data,
    };
  } catch (error) {
    console.error('🔴 Error fetching API data:', error);
    return {
      success: false,
      data: [],
      error:
        error instanceof Error ? error.message : 'An unknown error occurred',
    };
  }
};
